// src/pages/Investments.jsx
import { useState } from "react";
import { ChevronDown, ChevronUp, TrendingUp, TrendingDown } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, Tooltip } from "recharts";
import { formatCurrency, formatPercent } from "../lib/utils";
import { useInvestments } from "../hooks/useFinancialData";
import { LoadingDots } from "../components/ui/primitives";
import { cn } from "../lib/utils";

const USER_ID = "gavin";

const DEMO_HOLDINGS = [
  { id: "NVDA", ticker: "NVDA", name: "NVIDIA Corp", shares: 14, fmv: 6300, costBasis: 5180, sector: "Technology" },
  { id: "AAPL", ticker: "AAPL", name: "Apple Inc", shares: 22, fmv: 3960, costBasis: 3620, sector: "Technology" },
  { id: "SPY", ticker: "SPY", name: "SPDR S&P 500 ETF", shares: 8, fmv: 2640, costBasis: 2360, sector: "Broad Market ETF" },
  { id: "TSLA", ticker: "TSLA", name: "Tesla Inc", shares: 10, fmv: 1380, costBasis: 1480, sector: "Consumer Discretionary" },
];

const SECTOR_COLORS = {
  Technology: "pill-blue",
  "Broad Market ETF": "pill-green",
  "Consumer Discretionary": "pill-amber",
  Energy: "pill-amber",
  Healthcare: "pill-purple",
  Financial: "pill-blue",
};

const DEMO_CHART = Array.from({ length: 30 }, (_, i) => ({
  day: i,
  value: 12000 + Math.sin(i * 0.3) * 800 + i * 80 + Math.random() * 200,
}));

async function fetchAISummary(holding) {
  try {
    const pl = holding.fmv - holding.costBasis;
    const plPct = ((pl / holding.costBasis) * 100).toFixed(1);

    const response = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "claude-sonnet-4-20250514",
        max_tokens: 1000,
        tools: [{ type: "web_search_20250305", name: "web_search" }],
        messages: [
          {
            role: "user",
            content: `Give me a 3-4 sentence AI summary for this stock position in a personal finance app. Be direct and useful, not generic.

Ticker: ${holding.ticker}
Company: ${holding.name}
Sector: ${holding.sector}
Shares: ${holding.shares}
Current FMV: $${holding.fmv}
Cost basis: $${holding.costBasis}
P/L: ${pl >= 0 ? "+" : ""}$${pl} (${plPct}%)

Include: what the company does (1 sentence), current position context (performance + why), one recent relevant note from the web if available. End with one watchpoint for this position. Keep it under 80 words.`,
          },
        ],
      }),
    });

    const data = await response.json();
    const text = data.content
      ?.filter((b) => b.type === "text")
      .map((b) => b.text)
      .join(" ");
    return text || "Summary unavailable.";
  } catch (err) {
    const pl = holding.fmv - holding.costBasis;
    const plPct = ((pl / holding.costBasis) * 100).toFixed(1);
    return `${holding.name} (${holding.ticker}) is in the ${holding.sector} sector. Your position of ${holding.shares} shares is currently ${pl >= 0 ? "up" : "down"} ${Math.abs(pl).toFixed(0)} (${plPct}%) from your cost basis of $${holding.costBasis}.`;
  }
}

function HoldingRow({ holding }) {
  const [expanded, setExpanded] = useState(false);
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  const pl = holding.fmv - (holding.costBasis || holding.fmv);
  const plPct = holding.costBasis ? (pl / holding.costBasis) * 100 : 0;
  const isUp = pl >= 0;
  const sectorClass = SECTOR_COLORS[holding.sector] || "pill-blue";

  async function handleExpand() {
    const willExpand = !expanded;
    setExpanded(willExpand);
    if (willExpand && !summary) {
      setLoadingSummary(true);
      const result = await fetchAISummary(holding);
      setSummary(result);
      setLoadingSummary(false);
    }
  }

  return (
    <div className="border-b border-border last:border-0">
      <button
        onClick={handleExpand}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-background-elevated/50 transition-colors"
      >
        {/* Ticker badge */}
        <div className="w-10 h-10 rounded-xl bg-background-elevated border border-border flex items-center justify-center flex-shrink-0">
          <span className="text-[10px] font-bold font-mono text-text-primary">{holding.ticker}</span>
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold text-text-primary">{holding.ticker}</p>
          <p className="text-[11px] text-text-tertiary truncate">{holding.shares} shares · {holding.sector}</p>
        </div>

        <div className="text-right flex-shrink-0 mr-2">
          <p className="text-[14px] font-semibold text-text-primary font-tabular">
            {formatCurrency(holding.fmv)}
          </p>
          <p className={cn("text-[11px] font-medium font-tabular", isUp ? "text-accent-green" : "text-accent-red")}>
            {isUp ? "+" : ""}{formatCurrency(pl)} · {isUp ? "+" : ""}{plPct.toFixed(1)}%
          </p>
        </div>

        {expanded ? (
          <ChevronUp size={14} className="text-text-tertiary flex-shrink-0" />
        ) : (
          <ChevronDown size={14} className="text-text-tertiary flex-shrink-0" />
        )}
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="mx-4 mb-3 bg-background-elevated rounded-2xl p-4 border border-border">
          {/* Sector badge */}
          <span className={cn("pill mb-3 inline-flex", sectorClass)}>{holding.sector}</span>

          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <p className="label mb-1">Shares</p>
              <p className="text-sm font-semibold text-text-primary">{holding.shares}</p>
            </div>
            <div>
              <p className="label mb-1">FMV</p>
              <p className="text-sm font-semibold text-text-primary font-tabular">{formatCurrency(holding.fmv)}</p>
            </div>
            <div>
              <p className="label mb-1">Cost basis</p>
              <p className="text-sm font-semibold text-text-primary font-tabular">
                {holding.costBasis ? formatCurrency(holding.costBasis) : "—"}
              </p>
            </div>
            <div>
              <p className="label mb-1">P / L</p>
              <p className={cn("text-sm font-semibold font-tabular", isUp ? "text-accent-green" : "text-accent-red")}>
                {isUp ? "+" : ""}{formatCurrency(pl)} ({isUp ? "+" : ""}{plPct.toFixed(1)}%)
              </p>
            </div>
          </div>

          {/* AI Summary */}
          <div className="border-t border-border pt-3">
            <p className="label mb-2">✦ AI summary</p>
            {loadingSummary ? (
              <div className="flex items-center gap-2">
                <LoadingDots />
                <span className="text-[12px] text-text-tertiary italic">Fetching latest data...</span>
              </div>
            ) : (
              <p className="text-[12px] text-text-secondary leading-relaxed">{summary}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function Investments() {
  const { holdings, loading } = useInvestments();
  const displayHoldings = holdings.length ? holdings : DEMO_HOLDINGS;

  const totalFMV = displayHoldings.reduce((s, h) => s + h.fmv, 0);
  const totalCost = displayHoldings.reduce((s, h) => s + (h.costBasis || h.fmv), 0);
  const totalPL = totalFMV - totalCost;
  const totalPLPct = totalCost ? (totalPL / totalCost) * 100 : 0;
  const isUp = totalPL >= 0;

  return (
    <div className="pb-6">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/90 backdrop-blur-md border-b border-border px-4 py-3">
        <h1 className="font-display text-lg font-bold text-text-primary">Investments</h1>
      </div>

      <div className="px-4 pt-4 space-y-4">
        {/* Portfolio summary */}
        <div className="card p-4">
          <p className="label mb-1">Portfolio value</p>
          <p className="font-display text-3xl font-bold text-text-primary font-tabular">
            {formatCurrency(totalFMV)}
          </p>
          <div className="flex items-center gap-2 mt-1 mb-4">
            {isUp ? <TrendingUp size={14} className="text-accent-green" /> : <TrendingDown size={14} className="text-accent-red" />}
            <span className={cn("text-sm font-medium font-tabular", isUp ? "text-accent-green" : "text-accent-red")}>
              {isUp ? "+" : ""}{formatCurrency(totalPL)} ({isUp ? "+" : ""}{totalPLPct.toFixed(1)}%) all time
            </span>
          </div>

          {/* Chart */}
          <div className="h-20 -mx-1">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={DEMO_CHART} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                <defs>
                  <linearGradient id="invGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00E5A0" stopOpacity={0.2} />
                    <stop offset="100%" stopColor="#00E5A0" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <div className="bg-background-elevated border border-border rounded-xl px-3 py-2 text-xs">
                        <p className="text-text-primary font-semibold font-tabular">
                          {formatCurrency(payload[0].value)}
                        </p>
                      </div>
                    );
                  }}
                  cursor={false}
                />
                <Area type="monotone" dataKey="value" stroke="#00E5A0" strokeWidth={1.5} fill="url(#invGrad)" dot={false} activeDot={{ r: 3, fill: "#00E5A0", stroke: "#0A0A0F", strokeWidth: 2 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-[10px] text-text-tertiary font-mono">Apr 5</span>
            <span className="text-[10px] text-text-tertiary font-mono">May 5</span>
          </div>

          {/* Alt income */}
          <div className="mt-4 pt-3 border-t border-border flex justify-between items-center">
            <span className="text-[12px] text-text-secondary">Alternative income (dividends + gains)</span>
            <span className="text-sm font-semibold text-accent-green font-tabular">+$180</span>
          </div>
        </div>

        {/* Holdings */}
        <div>
          <p className="label px-1 mb-2">Holdings — tap for AI summary</p>
          <div className="card p-0 overflow-hidden">
            {displayHoldings.map((h) => (
              <HoldingRow key={h.id} holding={h} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
